import z from 'zod'
import { AMLocation, amGeometryMultiPolygonSchema, amGeometryPolygonSchema } from 'src/common-services'
import { circleSchema, circleWithNameSchema, multiPolygonWithNameSchema, polygonWithNameSchema } from 'src/types/map'
import { GooglePlace } from 'src/server/utils/place'

type UUID = string

export type UserList = {
  id: UUID
  name: string
  description?: string
  items: UserListItem[]
}

const userListAddressItemSchema = z.object({
  googlePlaceId: z.string(),
  name: z.string(),
  address: z.string(),
  googlePlace: z.custom<GooglePlace>().optional(),
})
export type UserListAddress = z.infer<typeof userListAddressItemSchema>

const userListLocationItemSchema = z.object({
  locationId: z.string(),
  name: z.string(),
  address: z.string(),
  location: z.custom<AMLocation>().optional(),
})
export type UserListLocation = z.infer<typeof userListLocationItemSchema>

export const userListCustomAreaItemSchema = polygonWithNameSchema
  .or(multiPolygonWithNameSchema)
  .or(circleWithNameSchema)
export type UserListCustomArea = z.infer<typeof userListCustomAreaItemSchema>

const userListParcelItemSchema = z.object({
  id: z.string().optional(),
  address: z.string().optional(),
  taxAssessorId: z.number().or(z.string()).optional(),
  mCode: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  /** attributes below are not present in the new parcel properties. We keep the type for already saved parcels in user lists */
  geometry: circleSchema.or(amGeometryPolygonSchema).or(amGeometryMultiPolygonSchema).optional(),
  properties: z
    .object({
      acres: z.number().optional(),
      addr: z.string().optional(),
      /** code is not present in the new parcel layer properties. We keep the type for already saved parcels in user lists */
      code: z.string().optional(),
      mCode: z.string().optional(),
      name: z.string().optional(),
      owner: z.string().optional(),
      sqft: z.number().optional(),
      tax_assessor_id: z.number().or(z.string()).optional(),
    })
    .optional(),
})
export type UserListParcel = z.infer<typeof userListParcelItemSchema>

const userOwnerItemSchema = z.object({
  ownerId: z.string(),
  name: z.string(),
  type: z.string(),
  address: z.string().optional(),
})
export type UserListOwner = z.infer<typeof userOwnerItemSchema>

export const userListItemSchema = z.union([
  z.object({
    id: z.string().optional(),
    type: z.literal('place'),
    data: userListAddressItemSchema,
  }),
  z.object({
    id: z.string().optional(),
    type: z.literal('location'),
    data: userListLocationItemSchema,
  }),
  z.object({
    id: z.string().optional(),
    type: z.literal('custom-area'),
    data: userListCustomAreaItemSchema,
  }),
  z.object({
    id: z.string().optional(),
    type: z.literal('parcel'),
    data: userListParcelItemSchema,
  }),
  z.object({
    id: z.string().optional(),
    type: z.literal('owner'),
    data: userOwnerItemSchema,
  }),
])
export type UserListItem = z.infer<typeof userListItemSchema>

export type UserListItemType = UserListItem['type']

export type UserListResponse = UserList

export type UserListsResponse = UserList[]

export type UpsertUserListRequest = {
  id?: string
  name: string
  description?: string
}

export const addItemsToListRequestSchema = z.array(
  z.object({
    type: z.literal('place'),
    data: z.object({
      googlePlaceId: z.string(),
      name: z.string(),
      address: z.string(),
    }),
  })
)
export type AddItemsToListRequest = z.infer<typeof addItemsToListRequestSchema>

export const recentlyViewedRequestSchema = z.object({
  types: z.array(z.enum(['place', 'location', 'custom-area', 'parcel', 'owner'])).optional(),
  limit: z.number().optional(),
})
export type RecentlyViewedRequest = z.infer<typeof recentlyViewedRequestSchema>
